import React, { useEffect } from "react";
import { Breadcrumb, Layout, Card, Row, Col, Button, theme, Spin, Alert, Tag, Descriptions, Typography, Space, Statistic, message, Modal } from "antd";
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useParams, useLocation } from "react-router-dom";
import { useScopedNavigate } from "../components/MarketerView/useScopedNavigate";
import { ArrowLeftOutlined, EditOutlined, PlayCircleOutlined, PauseCircleOutlined, BarChartOutlined, ShareAltOutlined, LinkOutlined, CheckCircleOutlined, FileOutlined, ReloadOutlined, DeleteOutlined, ExclamationCircleOutlined, BugOutlined, ExportOutlined, CopyOutlined } from "@ant-design/icons";
import { useGetSurveysQuery, useGetBrandsQuery, useUpdateSurveyStatusMutation, useGetSurveyResponsesQuery, useSoftDeleteSurveyMutation, useGetSurveyCohortsQuery } from "../services/nestjs-base-api";
import { ISurvey, IBrand } from "../services/nestjs-base-api/types";
import { Utils } from "../utils/utils";
import { renderFlagIcon, getLanguageName } from "../constants/countries";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const SurveyDetailsPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const navigate = useScopedNavigate();
    const location = useLocation();
    const { surveyHash } = useParams<{ surveyHash: string }>();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Fetch surveys to find the one with matching hash
    const { data: surveys, isLoading, error } = useGetSurveysQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Fetch brands for brand details
    const { data: brands } = useGetBrandsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Fetch cohorts for cohort name lookup
    const { data: cohorts = [] } = useGetSurveyCohortsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    const survey = surveys?.find((s: ISurvey) => s.hash === surveyHash);
    const brand = brands?.find((b: IBrand) => b.id === survey?.brandId);

    // Get cohortId from either direct property or nested cohort object
    const cohortId = survey?.cohortId || (survey as any)?.cohort?.id;

    // Helper function to get cohort name
    const getCohortName = (cohortId: number): string => {
        const cohort = cohorts.find(c => c.id === cohortId);
        return cohort?.displayName || 'Unknown Cohort';
    };

    // Fetch survey responses for statistics
    const { data: responses, isLoading: isLoadingResponses, refetch: refetchResponses } = useGetSurveyResponsesQuery({
        jwtToken: user?.jwtToken!,
        surveyId: survey?.id!
    }, {
        skip: !user?.jwtToken || !survey?.id
    });

    const [updateSurveyStatus, { isLoading: isUpdatingStatus }] = useUpdateSurveyStatusMutation();
    const [softDeleteSurvey, { isLoading: isDeleting }] = useSoftDeleteSurveyMutation();

    // Handle scroll to public URL section when coming from onboarding
    useEffect(() => {
        if (location.state?.scrollToPublicUrl && survey) {
            const timer = setTimeout(() => {
                const element = document.getElementById('public-url-section');
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500); // Small delay to ensure the page is fully rendered
            
            return () => clearTimeout(timer);
        }
    }, [location.state, survey]);

    // Calculate statistics from responses
    const statistics = React.useMemo(() => {
        if (!responses) {
            return { totalResponses: 0, completionRate: 0, accessCount: survey?.accessCount || 0 };
        }

        const totalResponses = responses.length;
        const completedResponses = responses.filter(r => r.questionResponses.length > 0).length;
        const completionRate = totalResponses > 0 ? Math.round((completedResponses / totalResponses) * 100) : 0;

        return { totalResponses, completionRate, accessCount: survey?.accessCount || 0 };
    }, [responses, survey]);

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'active': return 'green';
            case 'draft': return 'default';
            case 'paused': return 'orange';
            case 'completed': return 'blue';
            default: return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active': return <PlayCircleOutlined />;
            case 'paused': return <PauseCircleOutlined />;
            default: return null;
        }
    };

    const handleBack = () => {
        navigate('/dashboard/surveys');
    };

    const handleEdit = () => {
        navigate(`/dashboard/surveys/${surveyHash}/edit`);
    };

    const handleViewResults = () => {
        navigate(`/dashboard/surveys/${surveyHash}/results`);
    };

    const handleShare = () => {
        // Copy public survey URL to clipboard
        const publicUrl = Utils.getPublicSurveyUrl(surveyHash!);
        navigator.clipboard.writeText(publicUrl).then(() => {
            message.success(intl.formatMessage({ id: 'survey.details.urlCopied' }));
        });
    };

    const handleTestUrlCopy = () => {
        // Copy test survey URL to clipboard
        const testUrl = Utils.getTestSurveyUrl(surveyHash!);
        navigator.clipboard.writeText(testUrl).then(() => {
            message.success(intl.formatMessage({ id: 'survey.details.urlCopied' }));
        });
    };

    const handleTestUrlOpen = () => {
        // Open test survey URL in new tab
        const testUrl = Utils.getTestSurveyUrl(surveyHash!);
        window.open(testUrl, '_blank');
    };

    const handleRefresh = async () => {
        try {
            await refetchResponses();
            message.success('Statistics refreshed successfully');
        } catch (error) {
            message.error('Failed to refresh statistics');
        }
    };

    const handleStatusChange = async (newStatus: 'draft' | 'active' | 'paused' | 'completed') => {
        if (!survey) return;

        try {
            await updateSurveyStatus({
                jwtToken: user?.jwtToken!,
                surveyId: survey.id,
                status: newStatus
            }).unwrap();

            // Show success message based on the new status
            const messageKey = (() => {
                switch (newStatus) {
                    case 'active':
                        return survey.status === 'paused' ? 'survey.status.success.resumed' : 'survey.status.success.activated';
                    case 'paused':
                        return 'survey.status.success.paused';
                    case 'completed':
                        return 'survey.status.success.completed';
                    case 'draft':
                        return 'survey.status.success.draft';
                    default:
                        return 'survey.status.success.activated';
                }
            })();

            message.success(intl.formatMessage({ id: messageKey }));
        } catch (error) {
            console.error('Failed to update survey status:', error);
            message.error(intl.formatMessage({ id: 'survey.status.error' }));
        }
    };

    const handleDelete = () => {
        if (!survey) return;

        const surveyDisplayName = survey.name + (cohortId ? ` - ${getCohortName(cohortId)}` : '');

        Modal.confirm({
            title: intl.formatMessage({ id: 'survey.delete.confirm.title' }),
            content: intl.formatMessage({ id: 'survey.delete.confirm.content' }, { surveyName: surveyDisplayName }),
            icon: <ExclamationCircleOutlined />,
            okText: intl.formatMessage({ id: 'survey.delete.confirm.ok' }),
            cancelText: intl.formatMessage({ id: 'common.cancel' }),
            okType: 'danger',
            onOk: async () => {
                try {
                    await softDeleteSurvey({
                        jwtToken: user?.jwtToken!,
                        surveyId: survey.id
                    }).unwrap();

                    message.success(intl.formatMessage({ id: 'survey.delete.success' }));
                    navigate('/dashboard/surveys');
                } catch (error) {
                    console.error('Failed to delete survey:', error);
                    message.error(intl.formatMessage({ id: 'survey.delete.error' }));
                }
            }
        });
    };

    const handleDuplicate = () => {
        if (!survey) return;

        // Parse theme if it's a string
        let theme = {};
        if (survey.settings?.theme) {
            try {
                theme = typeof survey.settings.theme === 'string'
                    ? JSON.parse(survey.settings.theme)
                    : survey.settings.theme;
            } catch (error) {
                console.warn('Failed to parse theme JSON:', error);
            }
        }

        // Build prefill data with all survey settings (except country/cohort which user must select)
        const prefillData = {
            name: `${survey.name} (Copy)`,
            description: survey.description || '',
            brandId: survey.brandId,
            // Don't include cohortId or countryCode - user must select these
            languageCode: survey.languageCode || 'en',
            questionsPerResponse: survey.settings?.questionsPerResponse || 10,
            rotationStrategy: survey.settings?.rotationStrategy || 'balanced',
            theme,
            showProgressBar: survey.settings?.showProgressBar ?? true,
            animationSpeed: survey.settings?.animationSpeed || 800,
            allowSkip: survey.settings?.allowSkip ?? false,
            allowBackNavigation: survey.settings?.allowBackNavigation ?? true,
            persistProgress: survey.settings?.persistProgress ?? true,
            expirationHours: survey.settings?.expirationHours || 24,
            completionTitle: survey.settings?.completionTitle || 'Survey Complete!',
            completionSubtitle: survey.settings?.completionSubtitle || 'Thank you for taking the time to share your feedback...',
            completionButtonText: survey.settings?.completionButtonText || 'Visit our website',
            completionButtonLink: survey.settings?.completionButtonLink || '',
            showCompletionButton: survey.settings?.showCompletionButton ?? false,
            completionSecondaryButtonText: survey.settings?.completionSecondaryButtonText || 'Leave us a review',
            completionSecondaryButtonLink: survey.settings?.completionSecondaryButtonLink || '',
            showCompletionSecondaryButton: survey.settings?.showCompletionSecondaryButton ?? false,
            collectEmail: survey.settings?.collectEmail ?? true,
            emailRequired: survey.settings?.emailRequired ?? true,
            emailPlaceholder: survey.settings?.emailPlaceholder || 'Enter your email address',
            npsRequired: survey.settings?.npsRequired ?? true,
            showWelcomePage: survey.settings?.showWelcomePage ?? true,
        };

        navigate('/dashboard/surveys/create', { state: { prefillData } });
    };

    // Get available status transitions based on current status
    const getStatusTransitions = (currentStatus: string) => {
        const transitions: Array<{
            status: 'draft' | 'active' | 'paused' | 'completed';
            label: string;
            icon: React.ReactNode;
            type: 'primary' | 'default' | 'dashed';
        }> = [];

        switch (currentStatus) {
            case 'draft':
                transitions.push({
                    status: 'active',
                    label: intl.formatMessage({ id: 'survey.status.activate' }),
                    icon: <PlayCircleOutlined />,
                    type: 'primary'
                });
                break;
            case 'active':
                transitions.push(
                    {
                        status: 'paused',
                        label: intl.formatMessage({ id: 'survey.status.pause' }),
                        icon: <PauseCircleOutlined />,
                        type: 'default'
                    },
                    {
                        status: 'completed',
                        label: intl.formatMessage({ id: 'survey.status.complete' }),
                        icon: <CheckCircleOutlined />,
                        type: 'default'
                    }
                );
                break;
            case 'paused':
                transitions.push(
                    {
                        status: 'active',
                        label: intl.formatMessage({ id: 'survey.status.resume' }),
                        icon: <PlayCircleOutlined />,
                        type: 'primary'
                    },
                    {
                        status: 'completed',
                        label: intl.formatMessage({ id: 'survey.status.complete' }),
                        icon: <CheckCircleOutlined />,
                        type: 'default'
                    }
                );
                break;
            case 'completed':
                transitions.push(
                    {
                        status: 'active',
                        label: intl.formatMessage({ id: 'survey.status.reactivate' }),
                        icon: <PlayCircleOutlined />,
                        type: 'primary'
                    },
                    {
                        status: 'draft',
                        label: intl.formatMessage({ id: 'survey.status.markDraft' }),
                        icon: <FileOutlined />,
                        type: 'dashed'
                    }
                );
                break;
        }

        return transitions;
    };

    if (isLoading) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin size="large" />
                </div>
            </Content>
        );
    }

    if (error || !survey) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ padding: 24 }}>
                    <Alert 
                        type="error" 
                        message={intl.formatMessage({ id: 'survey.details.notFound' })}
                        description={intl.formatMessage({ id: 'survey.details.notFoundDesc' })}
                        action={
                            <Button onClick={handleBack}>
                                {intl.formatMessage({ id: 'survey.details.backToSurveys' })}
                            </Button>
                        }
                    />
                </div>
            </Content>
        );
    }

    // Parse theme from settings
    let themeColors = {};
    if (survey.settings?.theme) {
        try {
            themeColors = JSON.parse(survey.settings.theme);
        } catch (error) {
            console.warn('Failed to parse theme JSON:', error);
        }
    }
    
    const publicUrl = Utils.getPublicSurveyUrl(surveyHash!);
    const testUrl = Utils.getTestSurveyUrl(surveyHash!);

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb 
                style={{ margin: '16px 0' }}
                items={[
                    { title: user?.organisation?.name || intl.formatMessage({ id: 'common.platformName' }) },
                    {
                        title: (
                            <Button type="link" onClick={handleBack} style={{ padding: 0, height: 'auto', lineHeight: 'inherit', verticalAlign: 'baseline' }}>
                                {intl.formatMessage({ id: 'section.surveys' })}
                            </Button>
                        )
                    },
                    { title: survey.name + (cohortId ? ` - ${getCohortName(cohortId)}` : '') }
                ]}
            />

            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={handleBack}
                            >
                                {intl.formatMessage({ id: 'common.back' })}
                            </Button>
                            <Tag color={getStatusColor(survey.status)} icon={getStatusIcon(survey.status)} style={{ fontSize: '14px' }}>
                                {intl.formatMessage({ id: `surveys.status.${survey.status}` })}
                            </Tag>
                        </div>
                        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
                            {survey.name}{cohortId ? ` - ${getCohortName(cohortId)}` : ''}
                        </Title>
                        {survey.description && (
                            <Paragraph style={{ color: '#6b7280', fontSize: '16px' }}>
                                {survey.description}
                            </Paragraph>
                        )}
                    </div>
                    <Space>
                        <Button icon={<ShareAltOutlined />} onClick={handleShare}>
                            {intl.formatMessage({ id: 'survey.details.share' })}
                        </Button>
                        <Button icon={<CopyOutlined />} onClick={handleDuplicate}>
                            {intl.formatMessage({ id: 'survey.details.duplicate' })}
                        </Button>
                        <Button icon={<EditOutlined />} onClick={handleEdit}>
                            {intl.formatMessage({ id: 'survey.details.edit' })}
                        </Button>

                        {/* Status transition buttons */}
                        {getStatusTransitions(survey.status).map((transition, index) => (
                            <Button
                                key={transition.status}
                                type={transition.type}
                                icon={transition.icon}
                                loading={isUpdatingStatus}
                                onClick={() => handleStatusChange(transition.status)}
                            >
                                {transition.label}
                            </Button>
                        ))}
                        
                        {(survey.status === 'active' || survey.status === 'completed') && (
                            <Button type="primary" icon={<BarChartOutlined />} onClick={handleViewResults}>
                                {intl.formatMessage({ id: 'survey.details.viewResults' })}
                            </Button>
                        )}
                        <Button 
                            danger 
                            icon={<DeleteOutlined />} 
                            onClick={handleDelete}
                            loading={isDeleting}
                        >
                            {intl.formatMessage({ id: 'survey.details.delete' })}
                        </Button>
                    </Space>
                </div>

                <Row gutter={[24, 24]}>
                    {/* Survey Details */}
                    <Col xs={24} lg={16}>
                        <Card title={intl.formatMessage({ id: 'survey.details.details' })} style={{ marginBottom: 24 }}>
                            <Descriptions column={2} layout="vertical">
                                <Descriptions.Item label={intl.formatMessage({ id: 'survey.details.brand' })}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                backgroundColor: brand?.primaryColor || '#4338ca'
                                            }}
                                        />
                                        {survey.brand?.name || 'Unknown Brand'}
                                    </div>
                                </Descriptions.Item>
                                {cohortId && (
                                    <Descriptions.Item label="Cohort">
                                        <Tag color={getCohortName(cohortId) === 'All Customers' ? 'green' : 'blue'}>
                                            {getCohortName(cohortId)}
                                        </Tag>
                                    </Descriptions.Item>
                                )}
                                {survey.countryCode && (
                                    <Descriptions.Item label="Country">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {renderFlagIcon(survey.countryCode, 20)}
                                        </div>
                                    </Descriptions.Item>
                                )}
                                {survey.languageCode && (
                                    <Descriptions.Item label="Language">
                                        {getLanguageName(survey.languageCode)}
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label={intl.formatMessage({ id: 'survey.details.questionsPerResponse' })}>
                                    {survey.settings?.questionsPerResponse || 10} {intl.formatMessage({ id: 'survey.details.questions' })}
                                </Descriptions.Item>
                                {/*
                                <Descriptions.Item label={intl.formatMessage({ id: 'survey.details.rotationStrategy' })}>
                                    {intl.formatMessage({ id: `survey.form.rotationStrategy.${survey.settings?.rotationStrategy || 'balanced'}` })}
                                </Descriptions.Item>
                                */}
                                <Descriptions.Item label={intl.formatMessage({ id: 'survey.details.created' })}>
                                    {new Date(survey.createdAt).toLocaleDateString()}
                                </Descriptions.Item>
                                {survey.startsAt && (
                                    <Descriptions.Item label={intl.formatMessage({ id: 'survey.details.startDate' })}>
                                        {new Date(survey.startsAt).toLocaleDateString()}
                                    </Descriptions.Item>
                                )}
                                {survey.endsAt && (
                                    <Descriptions.Item label={intl.formatMessage({ id: 'survey.details.endDate' })}>
                                        {new Date(survey.endsAt).toLocaleDateString()}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>

                        {/* Public URL */}
                        <Card id="public-url-section" title={intl.formatMessage({ id: 'survey.details.publicUrl' })}>
                            <div style={{ marginBottom: 16 }}>
                                <Paragraph style={{ marginBottom: 12, color: '#6b7280' }}>
                                    {intl.formatMessage({ id: 'survey.details.publicUrl.instructions' })}
                                </Paragraph>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, backgroundColor: '#f8fafc', borderRadius: 8 }}>
                                    <LinkOutlined style={{ color: '#6b7280' }} />
                                    <Text code style={{ flex: 1, margin: 0 }}>
                                        {publicUrl}
                                    </Text>
                                    <Button size="small" onClick={handleShare}>
                                        {intl.formatMessage({ id: 'survey.details.copy' })}
                                    </Button>
                                </div>
                            </div>
                            <Alert
                                type="info"
                                showIcon
                                message={intl.formatMessage({ id: 'survey.details.publicUrl.customerIdentifier.title' })}
                                description={
                                    <div style={{ fontSize: '13px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            {intl.formatMessage({ id: 'survey.details.publicUrl.customerIdentifier.description' })}
                                        </div>
                                        <div style={{ marginTop: '12px', marginBottom: '8px', fontWeight: 500 }}>
                                            Platform-specific replacements for <Text code>{`{customer_identifier}`}</Text>:
                                        </div>
                                        <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                                            <li><strong>Klaviyo:</strong> Replace with <Text code>{`{{ email }}`}</Text></li>
                                            <li><strong>Mailchimp:</strong> Replace with <Text code>*|EMAIL|*</Text></li>
                                            <li><strong>HubSpot:</strong> Replace with <Text code>{`{{ contact.email }}`}</Text></li>
                                        </ul>
                                    </div>
                                }
                                style={{ marginBottom: 12 }}
                            />
                            <Alert
                                type="success"
                                showIcon
                                message={intl.formatMessage({ id: 'survey.details.publicUrl.crmIntegration.title' })}
                                description={intl.formatMessage({ id: 'survey.details.publicUrl.crmIntegration.description' })}
                            />
                        </Card>

                        <div style={{ marginBottom: 16 }}></div>

                        {/* Test URL */}
                        <Card title={intl.formatMessage({ id: 'survey.details.testUrl' })} style={{ marginBottom: 24, backgroundColor: '#fff7e6', borderColor: '#ffa940' }}>
                            <div style={{ marginBottom: 16 }}>
                                <Paragraph style={{ marginBottom: 12, color: '#6b7280' }}>
                                    {intl.formatMessage({ id: 'survey.details.testUrl.instructions' })}
                                </Paragraph>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, backgroundColor: '#f8fafc', borderRadius: 8 }}>
                                    <BugOutlined style={{ color: '#6b7280' }} />
                                    <Text code style={{ flex: 1, margin: 0 }}>
                                        {testUrl}
                                    </Text>
                                    <Space>
                                        <Button size="small" onClick={handleTestUrlCopy}>
                                            {intl.formatMessage({ id: 'survey.details.testUrl.copy' })}
                                        </Button>
                                        <Button size="small" icon={<ExportOutlined />} onClick={handleTestUrlOpen}>
                                            {intl.formatMessage({ id: 'survey.details.testUrl.open' })}
                                        </Button>
                                    </Space>
                                </div>
                            </div>
                            <Alert
                                type="warning"
                                showIcon
                                message={intl.formatMessage({ id: 'survey.details.testUrl.warning.title' })}
                                description={intl.formatMessage({ id: 'survey.details.testUrl.warning.description' })}
                            />
                        </Card>
                    </Col>

                    {/* Statistics & Theme */}
                    <Col xs={24} lg={8}>
                        <Card 
                            title={intl.formatMessage({ id: 'survey.details.statistics' })} 
                            extra={
                                <Button 
                                    size="small" 
                                    icon={<ReloadOutlined />} 
                                    loading={isLoadingResponses}
                                    onClick={handleRefresh}
                                />
                            }
                            style={{ marginBottom: 24 }}
                        >
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Statistic
                                        title={intl.formatMessage({ id: 'survey.details.responses' })}
                                        value={statistics.totalResponses}
                                        suffix={intl.formatMessage({ id: 'survey.details.total' })}
                                        loading={isLoadingResponses}
                                    />
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title={intl.formatMessage({ id: 'survey.details.linkAccesses' })}
                                        value={statistics.accessCount}
                                        suffix={intl.formatMessage({ id: 'survey.details.accesses' })}
                                        loading={isLoadingResponses}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* Theme Preview */}
                        <Card title={intl.formatMessage({ id: 'survey.details.theme' })}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                                {Object.entries(themeColors).map(([key, color]) => (
                                    <div key={key} style={{ textAlign: 'center' }}>
                                        <div
                                            style={{
                                                width: '100%',
                                                height: 32,
                                                backgroundColor: color as string,
                                                borderRadius: 4,
                                                border: '1px solid #e5e7eb',
                                                marginBottom: 4
                                            }}
                                        />
                                        <Text style={{ fontSize: '10px', color: '#6b7280' }}>
                                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Content>
    );
};

export default SurveyDetailsPage;